import React, { useState, useEffect } from 'react'
import { Icon } from '../../atoms/Icon'
import { ImageGalleryProps } from './ImageGallery.types'
import {
  GalleryContainer,
  GalleryWrapper,
  GalleryTrack,
  GallerySlide,
  GalleryImage,
  ImageCounter,
  GalleryControls,
  GalleryButton,
  ThumbnailStrip,
  Thumbnail,
} from './ImageGallery.styles'

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  currentIndex: controlledIndex,
  onIndexChange,
  showThumbnails = true,
  showControls = true,
  showCounter = true,
  height,
  className,
}) => {
  const [internalIndex, setInternalIndex] = useState(0)
  const currentIndex =
    controlledIndex !== undefined ? controlledIndex : internalIndex

  const handleIndexChange = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < images.length) {
      if (controlledIndex === undefined) {
        setInternalIndex(newIndex)
      }
      onIndexChange?.(newIndex)
    }
  }

  const nextImage = () => {
    handleIndexChange((currentIndex + 1) % images.length)
  }

  const previousImage = () => {
    handleIndexChange((currentIndex - 1 + images.length) % images.length)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        previousImage()
      } else if (e.key === 'ArrowRight') {
        nextImage()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex])

  if (!images || images.length === 0) {
    return null
  }

  return (
    <GalleryContainer $height={height} className={className}>
      <GalleryWrapper>
        <GalleryTrack $currentIndex={currentIndex}>
          {images.map((image, index) => (
            <GallerySlide key={index}>
              <GalleryImage
                src={image}
                alt={`Image ${index + 1}`}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </GallerySlide>
          ))}
        </GalleryTrack>

        {images.length > 1 && (
          <>
            {showControls && (
              <GalleryControls>
                <GalleryButton
                  onClick={previousImage}
                  aria-label="Previous image"
                >
                  <Icon name="arrow-left" size="medium" />
                </GalleryButton>
                <GalleryButton onClick={nextImage} aria-label="Next image">
                  <Icon name="arrow-right" size="medium" />
                </GalleryButton>
              </GalleryControls>
            )}

            {showCounter && (
              <ImageCounter>
                {currentIndex + 1} / {images.length}
              </ImageCounter>
            )}

            {showThumbnails && (
              <ThumbnailStrip>
                {images.map((image, index) => (
                  <Thumbnail
                    key={index}
                    $active={index === currentIndex}
                    onClick={() => handleIndexChange(index)}
                    aria-label={`Go to image ${index + 1}`}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  </Thumbnail>
                ))}
              </ThumbnailStrip>
            )}
          </>
        )}
      </GalleryWrapper>
    </GalleryContainer>
  )
}
